// Boot.
//
// The Astryx stylesheets are imported here — the same two imports, in the same
// place, as the engine client's src/render/ui/main.tsx. That file is the client
// the forge SERVES at /:org/:kb/dashboard/:format, so importing the same token
// layer is what makes the shell and the thing it frames one product rather than
// two vocabularies (Forge q3).
import '@astryxdesign/core/astryx.css';
import '@astryxdesign/charts/charts.css';
import './shell/shell.css';

import { StrictMode, useCallback, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';

import { Theme } from '@astryxdesign/core/theme';

import { App } from './App';
import { docujointTheme } from './theme';
import {
  artifactThemeMemory,
  browserThemeMemory,
  chooseMode,
  resolveTheme,
  type ThemeMode,
} from './theme-mode';

/**
 * The theme mode, held here because `<Theme>` is here.
 *
 * E15 q1 was answered "Ship a toggle" — NOT the recommended "follow the OS, no
 * toggle" — so the mode is state rather than a constant, the choice is
 * remembered, and the control lives in the chrome (AppShell). `system` stays one
 * of the three: a two-state switch would have to pick a side on first load and
 * would override the reader's OS preference before they had said anything.
 */
function Root() {
  const [mode, setMode] = useState<ThemeMode>(() => chooseMode(browserThemeMemory.read()));

  // THE DOCUMENT ROOT FOLLOWS THE MODE (2026-08-07, phone testing): html/body
  // and #root read light-dark() tokens (shell.css), but <Theme> sets
  // color-scheme on ITS OWN subtree — everything outside it resolved LIGHT
  // regardless of the toggle, which is the white band a phone shows beyond the
  // app's painted height. One property on the root closes the seam; 'system'
  // maps to 'light dark' so the UA keeps choosing.
  useEffect(() => {
    document.documentElement.style.colorScheme = mode === 'system' ? 'light dark' : mode;
  }, [mode]);

  // THE ARTIFACT FOLLOWS THE APP (Felipe's ask, 2026-08-07). The framed
  // dashboard reads its theme from shared same-origin storage (theme-mode.ts
  // says the whole story), and it knows light|dark only — so `system` is
  // resolved here, live: the media query is watched while it is the one
  // deciding, and an OS flip re-resolves without a reload. The resolved value
  // is written for the NEXT artifact load and handed down so an OPEN frame
  // re-keys immediately (App → DashboardView).
  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(
    () => window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false,
  );
  useEffect(() => {
    const query = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!query) return; // jsdom — resolution stays at its boot value
    const follow = (event: MediaQueryListEvent) => setSystemPrefersDark(event.matches);
    query.addEventListener('change', follow);
    return () => query.removeEventListener('change', follow);
  }, []);
  const resolvedTheme = resolveTheme(mode, systemPrefersDark);
  useEffect(() => {
    artifactThemeMemory.write(resolvedTheme);
  }, [resolvedTheme]);

  // A PICKER, not a cycle (Felipe's verdict, 2026-08-07): the menu offers the
  // three modes and a choice is set directly. nextMode stays a pure util; the
  // control that cycled through it retired.
  const select = useCallback((next: ThemeMode) => {
    browserThemeMemory.write(next);
    setMode(next);
  }, []);

  return (
    /*
      NOT OPTIONAL, and not merely a preference switch — see src/theme.ts.
      `Theme` applies the class that defines `--lightningcss-light` /
      `--lightningcss-dark`, and without it LightningCSS's `light-dark()`
      polyfill leaves EVERY `--color-*` token resolving to two colours
      concatenated ("#0064e0#2694fe"), which is not a colour. The app rendered
      white-on-transparent controls for that reason alone.

      `mode` is what resolves which half of each `light-dark()` pair applies:
      'light' and 'dark' pin `color-scheme`, 'system' sets `light dark` and lets
      the browser follow the OS.
    */
    <Theme theme={docujointTheme} mode={mode}>
      <App themeMode={mode} resolvedTheme={resolvedTheme} onSelectThemeMode={select} />
    </Theme>
  );
}

const root = document.getElementById('root');
if (root) {
  createRoot(root).render(
    <StrictMode>
      <Root />
    </StrictMode>,
  );
}
